// src/lib/firebase.ts
/// <reference types="vite/client" />
import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import { getAuth, initializeAuth, Auth, setPersistence, browserLocalPersistence, browserSessionPersistence, browserPopupRedirectResolver, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { getAnalytics, Analytics } from 'firebase/analytics';
import { getFunctions, Functions } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaEnterpriseProvider, CustomProvider, AppCheck, type AppCheckToken } from 'firebase/app-check';
import { firebaseConfig, firebaseAppName } from '../config/firebaseConfig';
import { isPresentedWindow } from './presenter/presentedWindow';

export interface ConfigState {
  isValid: boolean;
  missing: string[];
  present: Record<string, string>;
}

let configState: ConfigState | null = null;

function validateConfig(): ConfigState {
  const requiredKeys = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
  const missing: string[] = [];
  const present: Record<string, string> = {};

  requiredKeys.forEach((key) => {
    const val = (firebaseConfig as Record<string, string>)[key];
    if (!val || val === '') {
      missing.push(key);
    } else {
      present[key] = key === 'apiKey' ? val.slice(0, 10) + '...' : val;
    }
  });

  // The config table (apiKey prefix, authDomain, projectId, bucket, sender id, appId) used to be
  // printed with console.table on every page of every build, production included. Local dev only now.
  if (import.meta.env.DEV) {
    if (Object.keys(present).length > 0) console.table(present);
    console.log('[Firebase] config loaded:', { missingCount: missing.length, projectId: firebaseConfig.projectId });
  }

  const isValid = missing.length === 0;

  if (!isValid) {
    const errMsg = `❌ Firebase Config Error: Missing required keys in firebaseConfig: ${missing.join(', ')}`;
    console.error(errMsg);
    if (typeof window !== 'undefined') {
      (window as any).__FIREBASE_CONFIG_ERROR__ = { missing, message: errMsg };
    }
  } else {
    if (import.meta.env.DEV) console.log('✅ Firebase config valid');
    if (typeof window !== 'undefined') {
      delete (window as any).__FIREBASE_CONFIG_ERROR__;
    }
  }

  configState = { isValid, missing, present };
  return configState;
}

const validationResult = validateConfig();

export function getConfigState(): ConfigState {
  return configState || validateConfig();
}

export function isConfigValid(): boolean {
  return getConfigState().isValid;
}

// PROJECT MISMATCH CHECK
const EXPECTED_PROJECT_ID = firebaseAppName;
if (firebaseConfig.projectId && firebaseConfig.projectId !== EXPECTED_PROJECT_ID) {
  console.error(`[FirebaseConfig] ❌ WRONG PROJECT: Active ${firebaseConfig.projectId}, expected ${EXPECTED_PROJECT_ID}`);
}

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;
let storage: FirebaseStorage | null = null;
let functions: Functions | null = null;
let appCheck: AppCheck | null = null;

/**
 * Resolves once App Check has been initialized — or once it is known it never will be (no site
 * key, prerender capture, init failure). App Check is now initialized synchronously during this
 * module's init (below), before Firestore/Storage are created, so the SDKs wait for a token before
 * their first request. Callers that awaited this to dodge the old `load`-event deferral (public
 * job posting, public job board, public company profile, AuthContext's denied-read verification)
 * still can; it resolves immediately once init has run. Never rejects; a 6 s ceiling means a page
 * is never held hostage by it.
 */
/**
 * ROOT-CAUSED 2026-09-26 ("Network error. Check your connection." on /signin with a working
 * network). ReCaptchaEnterpriseProvider loads https://www.google.com/recaptcha/enterprise.js with an
 * onload handler and NO onerror (@firebase/app-check 0.11.0, loadReCAPTCHAEnterpriseScript). When an
 * ad blocker, a privacy extension or a network filter blocks that script, the provider's
 * "initialized" promise never settles, App Check's getToken() never returns, and Firebase Auth —
 * which waits for an App Check token before every identitytoolkit request — hits its own 30 s
 * request timeout and reports auth/network-request-failed. No sign-in request is ever sent.
 * Reproduced on production by aborting only that one script (scripts/tmp-repro-institute-signin.mjs
 * --block=recaptcha): 50 s, then the message; unblocked, the same sign-in lands in ~8 s.
 *
 * Fix: load the script ourselves with onerror and a ceiling, and hand App Check a CustomProvider
 * that waits for THAT load before delegating to the real ReCaptchaEnterpriseProvider (which finds
 * grecaptcha.enterprise already present and skips its own loader). If the script fails or is slow,
 * getToken rejects promptly; the App Check SDK then returns "no token" and Auth/Firestore proceed
 * without one — exactly what happens today on a 403 exchange. On an App Check-ENFORCED project
 * those requests are then denied with a clear error instead of a 30 s hang; on production (not
 * enforced) they simply succeed. Once the script is up, every later getToken works normally.
 */
/*
 * "reCAPTCHA loads three times per page" (QA 2026-10-05) is NOT three App Check initializations.
 * App Check is initialized once per page load (one Firebase app, one initializeAppCheck() behind the
 * __APP_CHECK_INITIALIZED__ guard, skipped during prerender), which means one enterprise.js tag, one
 * #fire_app_check_[DEFAULT] div and ONE invisible widget. reCAPTCHA itself then runs its runtime,
 * recaptcha__en.js (~850 KB, ~347 KiB over the wire), in three places: this page, the widget's
 * anchor iframe and its challenge (bframe) iframe. Measured 2026-10-05 in Chromium with Google's
 * public reCAPTCHA test key: first visit = 1 network download (356,031 bytes) + 2
 * disk-cache hits with 0 bytes transferred; repeat visit = 0 bytes. Changing that would mean
 * replacing the SDK's provider, not fixing a double init, so it is left as is.
 */
export const RECAPTCHA_SCRIPT_TIMEOUT_MS = 10_000;
const RECAPTCHA_ENTERPRISE_URL = 'https://www.google.com/recaptcha/enterprise.js';
function loadRecaptchaEnterpriseScript(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const w = window as unknown as { grecaptcha?: { enterprise?: unknown } };
    if (w.grecaptcha?.enterprise) return resolve();
    const timer = window.setTimeout(
      () => reject(new Error(`reCAPTCHA Enterprise script did not load within ${RECAPTCHA_SCRIPT_TIMEOUT_MS} ms`)),
      RECAPTCHA_SCRIPT_TIMEOUT_MS,
    );
    const script = document.createElement('script');
    script.src = RECAPTCHA_ENTERPRISE_URL;
    script.async = true;
    script.onload = () => { window.clearTimeout(timer); resolve(); };
    script.onerror = () => { window.clearTimeout(timer); reject(new Error('reCAPTCHA Enterprise script failed to load (blocked by an extension or a network filter, or offline)')); };
    document.head.appendChild(script);
  });
}
/** The provider's own initialize()/getToken() — the AppCheckProvider contract, omitted from the public typings. */
type AppCheckProviderInternal = { initialize(app: FirebaseApp): void; getToken(): Promise<AppCheckToken> };
function guardedRecaptchaProvider(firebaseApp: FirebaseApp, siteKey: string): CustomProvider {
  const enterprise = new ReCaptchaEnterpriseProvider(siteKey) as unknown as AppCheckProviderInternal;
  const scriptReady = loadRecaptchaEnterpriseScript();
  let warned = false;
  scriptReady.catch((err) => {
    if (warned) return;
    warned = true;
    console.warn('[Firebase] App Check unavailable for this session — reCAPTCHA did not load; continuing without it:', err instanceof Error ? err.message : err);
  });
  let initialized = false;
  return new CustomProvider({
    getToken: async () => {
      await scriptReady; // rejects promptly when blocked — never hangs the caller
      if (!initialized) {
        enterprise.initialize(firebaseApp);
        initialized = true;
      }
      return enterprise.getToken();
    },
  });
}

let markAppCheckSettled: () => void = () => {};
export const appCheckSettled: Promise<void> = new Promise<void>((resolveSettled) => {
  markAppCheckSettled = resolveSettled;
  if (typeof window === 'undefined') resolveSettled();
  else window.setTimeout(resolveSettled, 6000);
});

if (validationResult.isValid) {
  try {
    app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
    if (import.meta.env.DEV) console.log('[Firebase] app initialized:', firebaseConfig.projectId);

    // Ideally runs before the first Firestore/Storage/Functions request so that request
    // carries a valid App Check token from the start — see the `load`-event deferral below
    // for why that's not quite true in practice yet. A missing site key degrades to "no App
    // Check" rather than breaking the app — see .env.example for how VITE_RECAPTCHA_SITE_KEY
    // gets set per environment.
    const recaptchaSiteKey = (import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined)?.trim();
    const appCheckWindow = window as unknown as { __APP_CHECK_INITIALIZED__?: boolean; __PRERENDER__?: boolean };
    // ROOT-CAUSED 2026-08-28 (re-investigated for a hydrateRoot() migration that turned out to
    // be unrelated — see src/main.tsx). The actual bug: scripts/prerender-home.mjs's Playwright
    // capture runs this exact same client bundle to produce dist/index.html's static markup. If
    // App Check were allowed to initialize during that capture, ReCaptchaV3Provider creates a
    // `<div id="fire_app_check_[DEFAULT]">` placeholder as a direct child of <body> (outside
    // React's #root entirely, so no React mount strategy — createRoot or hydrateRoot — ever
    // touches it). That div got serialized into the shipped static HTML. A real visitor's
    // browser then runs initializeAppCheck() again and tries to create ANOTHER same-ID
    // placeholder, which is what "reCAPTCHA placeholder element must be empty" actually is —
    // confirmed by finding two `#fire_app_check_[DEFAULT]` divs in a live reproduction. Nothing
    // to do with the createRoot/hydrateRoot non-hydrating-teardown theory the previous
    // investigation landed on, which was a plausible-sounding but incorrect correlation (it
    // "reproduced only with prerendering involved" for the right reason — prerendering is what
    // bakes in the stray div — but the wrong mechanism). Fix: never run App Check during the
    // prerender capture itself, mirroring the same window.__PRERENDER__ check
    // MainLandingPage.tsx already uses to skip its entrance animation during capture.
    if (appCheckWindow.__PRERENDER__ === true) {
      if (import.meta.env.DEV) console.log('[Firebase] App Check skipped during prerender capture');
      markAppCheckSettled();
    } else if (recaptchaSiteKey && typeof window !== 'undefined' && !appCheckWindow.__APP_CHECK_INITIALIZED__) {
      appCheckWindow.__APP_CHECK_INITIALIZED__ = true;
      const initAppCheck = () => {
        try {
          if (import.meta.env.DEV && (import.meta.env.VITE_APPCHECK_DEBUG_TOKEN as string | undefined)) {
            // Local dev only: lets `npm run dev` pass App Check without a real reCAPTCHA
            // challenge running against localhost. Never set this env var outside dev.
            (window as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
              import.meta.env.VITE_APPCHECK_DEBUG_TOKEN as string;
          }
          // Registered in Firebase App Check as an ENTERPRISE key (confirmed via the App Check
          // Management API — recaptchaEnterpriseConfig has this siteKey, recaptchaV3Config does
          // not), not a standard v3 key. Using ReCaptchaV3Provider against an Enterprise-
          // registered key produced a real, reproducible 403 on the token exchange itself
          // (appCheck/initial-throttle) — a separate bug from the placeholder-collision one,
          // found while re-investigating this item.
          appCheck = initializeAppCheck(app!, {
            provider: guardedRecaptchaProvider(app!, recaptchaSiteKey),
            isTokenAutoRefreshEnabled: true,
          });
          if (import.meta.env.DEV) console.log('[Firebase] App Check initialized');
        } catch (err) {
          console.warn('⚠️ App Check failed to initialize (requests will proceed without it):', err);
        } finally {
          markAppCheckSettled();
        }
      };
      // Initialize NOW, before getFirestore()/getStorage() below, not on the window `load` event.
      // The deferral dated from the placeholder-collision investigation (eddd137, 2026-08-18),
      // whose real cause is handled by the __PRERENDER__ skip above. Deferring assumed signed-in
      // screens never notice ("restoring the session takes longer than `load`") — false for
      // in-app navigations: the session restores from IndexedDB in ~100 ms and the page's first
      // Firestore/Storage requests (profile listener, counts, uploads) went out before
      // initializeAppCheck() ran, so a project that enforces App Check denied them
      // (2026-09-25 staging: pages failing to load, "Upload unavailable", and AuthContext treating
      // the denial as a lapsed session). Once App Check is initialized, the SDKs wait for its
      // token before sending, which is the behaviour we want.
      initAppCheck();
    } else {
      // No site key (App Check disabled for this build) or already initialized (HMR): nothing to wait for.
      if (typeof window !== 'undefined' && !recaptchaSiteKey) {
        console.warn('[Firebase] VITE_RECAPTCHA_SITE_KEY not set — App Check disabled for this build');
      }
      markAppCheckSettled();
    }

    // A presenter-mode presented window (./presenter/presentedWindow) keeps its sign-in in this tab
    // only, so it can be a demo persona while the presenter's other tabs stay signed in as themselves.
    auth = isPresentedWindow
      ? initializeAuth(app, { persistence: browserSessionPersistence, popupRedirectResolver: browserPopupRedirectResolver })
      : getAuth(app);
    db = getFirestore(app);
    storage = getStorage(app);
    functions = getFunctions(app);
  } catch (err) {
    markAppCheckSettled();
    console.error('[Firebase] Failed to initialize:', err);
    if (typeof window !== 'undefined') {
      (window as any).__FIREBASE_CONFIG_ERROR__ = {
        missing: validationResult.missing,
        message: `Firebase initialization failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
      };
    }
  }
} else {
  markAppCheckSettled();
  console.warn('[Firebase] Skipping initialization due to invalid config');
}

export { auth, db, storage, functions, appCheck };

if (typeof window !== 'undefined' && auth && !isPresentedWindow) {
  setPersistence(auth, browserLocalPersistence).catch((err) => {
    console.warn('⚠️ Failed to set auth persistence:', err);
  });
}

let authReadyResolve!: () => void;
export const authReady = new Promise<void>((res) => {
  authReadyResolve = res;
  if (!auth) res();
});

if (auth) {
  onAuthStateChanged(auth, () => {
    if (authReadyResolve) authReadyResolve();
  });
} else {
  authReadyResolve();
}

export async function ensureAuthReady(): Promise<void> {
  await authReady;
}

export function getAnalyticsSafe(): Analytics | null {
  try {
    if (app && firebaseConfig.measurementId && typeof window !== 'undefined') {
      return getAnalytics(app);
    }
    return null;
  } catch (err) {
    console.warn('⚠️ Analytics not available:', err);
    return null;
  }
}

export { app };

export function getAuthSafe(): Auth {
  if (!auth) {
    throw new Error('Firebase Auth is not initialized.');
  }
  return auth;
}

export function getDbSafe(): Firestore {
  if (!db) {
    throw new Error('Firestore is not initialized.');
  }
  return db;
}

export function getStorageSafe(): FirebaseStorage {
  if (!storage) {
    throw new Error('Firebase Storage is not initialized.');
  }
  return storage;
}
